/**
 * SDK Guardrail Hook Bridge — generic adapter from guardrail check functions
 * + plugin guardrails to SDK-compatible hook responses.
 *
 * Embedded guardrail rules are converted via createGuardrailHooksFromRules(),
 * which delegates to createGuardrailHooks(). All hook logic lives here.
 */

import { pluginRegistry } from "./plugin.js";

export interface GuardrailCheckResult {
  guardrail_id: string;
  passed: boolean;
  action: "block" | "warn" | "shadow" | "info";
  message: string;
}

export interface HookResponse {
  permission?: "deny";
  user_message?: string;
  agent_message?: string;
  additionalContext?: string;
}

export interface GuardrailRule {
  guardrail_id: string;
  pattern: string;
  action: "block" | "warn" | "shadow" | "info";
  message: string;
  file_glob?: string;
  exclude_glob?: string;
}

export interface GuardrailRuleData {
  commandRules: GuardrailRule[];
  fileRules: GuardrailRule[];
  contentRules: GuardrailRule[];
}

export interface GuardrailCheckFunctions {
  checkCommand: (command: string) => GuardrailCheckResult[];
  checkFilePath: (filePath: string) => GuardrailCheckResult[];
  checkContent: (filePath: string, content: string) => GuardrailCheckResult[];
}

function globFragmentToRegex(fragment: string): string {
  let out = "";
  for (const ch of fragment) {
    if (ch === "*") out += "[^/]*";
    else if (ch === "?") out += "[^/]";
    else if (".+^$|()[]\\".includes(ch)) out += "\\" + ch;
    else out += ch;
  }
  return out;
}

export function matchGlob(pattern: string, path: string): boolean {
  let regex = "";
  let i = 0;
  while (i < pattern.length) {
    if (pattern[i] === "*" && pattern[i + 1] === "*" && pattern[i + 2] === "/") {
      regex += "(?:.+/)?";
      i += 3;
    } else if (pattern[i] === "*" && pattern[i + 1] === "*") {
      regex += ".*";
      i += 2;
    } else if (pattern[i] === "*") {
      regex += "[^/]*";
      i += 1;
    } else if (pattern[i] === "?") {
      regex += "[^/]";
      i += 1;
    } else if (pattern[i] === "{") {
      const end = pattern.indexOf("}", i);
      if (end !== -1) {
        const alts = pattern.slice(i + 1, end).split(",").map(globFragmentToRegex);
        regex += "(?:" + alts.join("|") + ")";
        i = end + 1;
      } else {
        regex += "\\{";
        i += 1;
      }
    } else if (".+^$|()[]\\".includes(pattern[i])) {
      regex += "\\" + pattern[i];
      i += 1;
    } else {
      regex += pattern[i];
      i += 1;
    }
  }
  return new RegExp("^" + regex + "$").test(path);
}

export interface HookContext {
  /** Agent id for per-agent guardrail filtering. */
  agentId?: string;
  command?: string;
  filePath?: string;
  content?: string;
}

export interface GuardrailHooks {
  beforeShellExecution: (input: { command?: string }) => HookResponse;
  preToolUse: (input: {
    tool_name?: string;
    tool_input?: { file_path?: string; path?: string };
  }) => HookResponse;
  afterFileEdit: (input: { file_path?: string; content?: string }) => HookResponse;
  runChecks: (ctx: HookContext) => GuardrailCheckResult[];
}

export function getBlockingViolations(results: GuardrailCheckResult[]): GuardrailCheckResult[] {
  return results.filter((r) => !r.passed && r.action === "block");
}

export function getWarnings(results: GuardrailCheckResult[]): GuardrailCheckResult[] {
  return results.filter((r) => !r.passed && r.action === "warn");
}

export function hasBlockingViolation(results: GuardrailCheckResult[]): boolean {
  return results.some((r) => !r.passed && r.action === "block");
}

function pluginResultToCheckResult(r: {
  matched: boolean;
  guardrail_id: string;
  action: string;
  message: string;
}): GuardrailCheckResult {
  return {
    guardrail_id: r.guardrail_id,
    passed: !r.matched,
    action: r.action as GuardrailCheckResult["action"],
    message: r.matched ? r.message : "",
  };
}

/**
 * Create SDK hook functions from generated guardrail check functions.
 *
 * Merges DSL-generated checks with plugin-registered guardrails and
 * returns hook functions in the format expected by the Claude SDK,
 * plus a generic runChecks() for programmatic use.
 */
export function createGuardrailHooks(checks: GuardrailCheckFunctions): GuardrailHooks {
  function runChecks(ctx: HookContext): GuardrailCheckResult[] {
    const results: GuardrailCheckResult[] = [];

    if (ctx.command) {
      results.push(...checks.checkCommand(ctx.command));
      results.push(
        ...pluginRegistry.evaluateCommandGuardrails(ctx.command).map(pluginResultToCheckResult),
      );
    }

    if (ctx.filePath) {
      results.push(...checks.checkFilePath(ctx.filePath));
      results.push(
        ...pluginRegistry.evaluateFilePathGuardrails(ctx.filePath).map(pluginResultToCheckResult),
      );
    }

    if (ctx.filePath && ctx.content) {
      results.push(...checks.checkContent(ctx.filePath, ctx.content));
      results.push(
        ...pluginRegistry
          .evaluateFileContentGuardrails(ctx.filePath, ctx.content)
          .map(pluginResultToCheckResult),
      );
    }

    return results;
  }

  function beforeShellExecution(input: { command?: string }): HookResponse {
    const command = input.command ?? "";
    if (!command) return {};

    const results = runChecks({ command });
    const blocks = getBlockingViolations(results);
    const warns = getWarnings(results);

    if (blocks.length > 0) {
      const msg = blocks.map((r) => `[${r.guardrail_id}] ${r.message}`).join("\n");
      return { permission: "deny", user_message: msg, agent_message: msg };
    }

    if (warns.length > 0) {
      return {
        additionalContext: warns.map((r) => `[${r.guardrail_id}] ${r.message}`).join("\n"),
      };
    }

    return {};
  }

  function preToolUse(input: {
    tool_name?: string;
    tool_input?: { file_path?: string; path?: string };
  }): HookResponse {
    const filePath = input.tool_input?.file_path ?? input.tool_input?.path ?? "";
    if (!filePath) return {};

    const results = runChecks({ filePath });
    const blocks = getBlockingViolations(results);

    if (blocks.length > 0) {
      const msg = blocks.map((r) => `[${r.guardrail_id}] ${r.message}`).join("\n");
      return { permission: "deny", user_message: msg, agent_message: msg };
    }

    return {};
  }

  function afterFileEdit(input: { file_path?: string; content?: string }): HookResponse {
    const filePath = input.file_path ?? "";
    const content = input.content ?? "";
    if (!filePath) return {};

    const results = runChecks({ filePath, content });
    const blocks = getBlockingViolations(results);

    if (blocks.length > 0) {
      const msg = blocks.map((r) => `[${r.guardrail_id}] ${r.message}`).join("\n");
      return { permission: "deny", user_message: msg, agent_message: msg };
    }

    return {};
  }

  return { beforeShellExecution, preToolUse, afterFileEdit, runChecks };
}

/**
 * Create SDK hook functions from embedded guardrail rule data.
 */
/**
 * Create SDK hooks scoped to a single agent from a per-agent guardrail map.
 * Returns empty-passing hooks when the agent has no rules in the map.
 */
export function createGuardrailHooksForAgent(
  agentId: string,
  perAgent: Map<string, GuardrailRuleData>,
): GuardrailHooks {
  const rules = perAgent.get(agentId);
  if (!rules) {
    return createGuardrailHooks({
      checkCommand: () => [],
      checkFilePath: () => [],
      checkContent: () => [],
    });
  }
  const hooks = createGuardrailHooksFromRules(rules);
  const baseRunChecks = hooks.runChecks;
  return {
    ...hooks,
    runChecks(ctx: HookContext) {
      return baseRunChecks({ ...ctx, agentId });
    },
  };
}

/** Compile a guardrail regex pattern, supporting inline `(?i)` case-insensitivity prefix. */
export function compileGuardrailPattern(pattern: string): RegExp {
  if (pattern.startsWith("(?i)")) {
    return new RegExp(pattern.slice(4), "i");
  }
  return new RegExp(pattern);
}

export function createGuardrailHooksFromRules(rules: GuardrailRuleData): GuardrailHooks {
  const commandChecks = rules.commandRules.map((rule) => ({
    guardrail_id: rule.guardrail_id,
    pattern: compileGuardrailPattern(rule.pattern),
    action: rule.action,
    message: rule.message,
  }));

  const fileChecks = rules.fileRules;

  const contentChecks = rules.contentRules.map((rule) => ({
    guardrail_id: rule.guardrail_id,
    pattern: compileGuardrailPattern(rule.pattern),
    action: rule.action,
    message: rule.message,
    file_glob: rule.file_glob,
    exclude_glob: rule.exclude_glob,
  }));

  return createGuardrailHooks({
    checkCommand(command: string): GuardrailCheckResult[] {
      const results: GuardrailCheckResult[] = [];
      for (const check of commandChecks) {
        const passed = !check.pattern.test(command);
        results.push({
          guardrail_id: check.guardrail_id,
          passed,
          action: check.action,
          message: passed ? "" : check.message,
        });
      }
      return results;
    },

    checkFilePath(filePath: string): GuardrailCheckResult[] {
      const results: GuardrailCheckResult[] = [];
      for (const check of fileChecks) {
        if (check.exclude_glob && matchGlob(check.exclude_glob, filePath)) {
          continue;
        }
        const matched = matchGlob(check.pattern, filePath);
        results.push({
          guardrail_id: check.guardrail_id,
          passed: !matched,
          action: check.action,
          message: matched ? check.message : "",
        });
      }
      return results;
    },

    checkContent(filePath: string, content: string): GuardrailCheckResult[] {
      const results: GuardrailCheckResult[] = [];
      for (const check of contentChecks) {
        if (check.file_glob && !matchGlob(check.file_glob, filePath)) {
          continue;
        }
        if (check.exclude_glob && matchGlob(check.exclude_glob, filePath)) {
          continue;
        }
        const matched = check.pattern.test(content);
        results.push({
          guardrail_id: check.guardrail_id,
          passed: !matched,
          action: check.action,
          message: matched ? check.message : "",
        });
      }
      return results;
    },
  });
}
